<template>
  <div class="auth-wrapper auth-v1">
    <div class="auth-inner m-0">
      <b-card class="mb-0">
        <b-link
          class="brand-logo"
          to="/"
        >
          <b-img
            class="w-50"
            :src="companyLogoImage"
            alt="App logo"
          />
        </b-link>
        <b-card-title class="mb-1">
          <!--Welcome!-->
        </b-card-title>
        <b-card-text class="mb-2">
<!--          Please sign-in to your account-->
        </b-card-text>

          <b-form
            v-if="!needsEmailVerification"
            class="auth-login-form mt-2"
            @submit.prevent
          >
            <b-form-group
              label-for="email"
              label="Email"
            >
                <b-form-input
                  id="email"
                  v-model="form.email"
                  name="login-email"
                  :state="null"
                  placeholder="john@example.com"
                  autofocus
                />
                <small class="text-danger">{{ '' }}</small>
            </b-form-group>
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label for="password">Password</label>
                <b-link :to="{ name: 'recovery' }">
                  <small>Forgot Password?</small>
                </b-link>
              </div>
                <b-input-group
                  class="input-group-merge"
                  :class="null"
                >
                  <b-form-input
                    id="password"
                    v-model="form.password"
                    :type="passwordFieldType"
                    class="form-control-merge"
                    :state="null"
                    name="login-password"
                    placeholder="············"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ '' }}</small>
            </b-form-group>

            <MathCaptchaField
              ref="loginCaptcha"
              input-id="login_captcha"
              :captcha-token="captchaToken"
              :captcha-answer="captchaAnswer"
              @update:captchaToken="captchaToken = $event"
              @update:captchaAnswer="captchaAnswer = $event"
            />

            <div v-if="availableAccounts.length" class="mb-1">
              <small class="text-muted d-block mb-50">Select account to continue:</small>
              <button
                v-for="(account, index) in availableAccounts"
                :key="`${account.company_id}-${account.role_name}-${index}`"
                type="button"
                class="btn btn-outline-secondary btn-block text-left mb-50"
                :disabled="isLoginLoading"
                @click="loginWithAvailableAccount(account)"
              >
                {{ account.company_name }} - {{ account.role_name }}
              </button>
            </div>

            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="isLoginLoading"
              @click="validationForm"
            >
              <b-spinner v-if="isLoginLoading" label="Spinning" small />
              <span v-else> Sign in </span>
            </b-button>

            <p class="text-center mt-2 mb-0">
              <b-link :to="{ name: 'register' }">
                Don't have an account?<br/>Register your company now
              </b-link>
            </p>
          </b-form>

          <b-form
            v-else
            class="auth-login-form mt-2"
            @submit.prevent="submitVerificationCode"
          >
            <b-card-text class="mb-1">
              Enter the 6-digit code we sent to
              <strong>{{ verificationEmail }}</strong>
              to verify your email and continue.
            </b-card-text>

            <b-form-group
              label-for="verification-code"
              label="Verification code"
            >
              <b-form-input
                id="verification-code"
                v-model="verificationCode"
                name="verification-code"
                placeholder="123456"
                maxlength="12"
                inputmode="numeric"
                autocomplete="one-time-code"
                autofocus
                @input="onVerificationCodeInput"
              />
              <small
                v-if="verificationError"
                class="text-danger d-block mt-25"
              >{{ verificationError }}</small>
            </b-form-group>

            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="isVerifyLoading || !verificationCode"
            >
              <b-spinner v-if="isVerifyLoading" label="Spinning" small />
              <span v-else>Verify and continue</span>
            </b-button>

            <div class="d-flex justify-content-between align-items-center mt-1">
              <b-button
                variant="link"
                class="p-0"
                :disabled="isResendLoading || resendCooldown > 0"
                @click="resendVerificationCode"
              >
                <b-spinner v-if="isResendLoading" small />
                <span v-else-if="resendCooldown > 0">Resend code ({{ resendCooldown }}s)</span>
                <span v-else>Resend code</span>
              </b-button>
              <b-button
                variant="link"
                class="p-0"
                @click="backToLogin"
              >
                Back to sign in
              </b-button>
            </div>
          </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
import {
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BSpinner,
  BCard,
} from "bootstrap-vue";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import store from "@/store/index";
import { mapActions } from "vuex";
import { $themeConfig } from "@themeConfig";
import { applyCompanyWindowGlobals } from '@/libs/companyGlobals'
import { getBrowserAccessContext } from '@/libs/accessLogContext'
import { sweetSuccess } from '@/libs/sweet-alerts'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import MathCaptchaField from '@/views/Components/Elements/MathCaptchaField.vue'

export default {
  components: {
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BSpinner,
    BCard,
    MathCaptchaField,
  },
  mixins: [togglePasswordVisibility],
  data() {
    const { companyLogoImage } = $themeConfig.app;
    return {
      status: "",
      form: {
        password: "",
        email: "",
      },
      sideImg: require("@/assets/images/pages/login-page.svg"),
      isLoginLoading: false,
      companyLogoImage,
      availableAccounts: [],
      selectedCompanyId: null,
      needsEmailVerification: false,
      verificationEmail: '',
      verificationCode: '',
      verificationError: '',
      isVerifyLoading: false,
      isResendLoading: false,
      resendCooldown: 0,
      resendTimer: null,
      captchaToken: '',
      captchaAnswer: '',
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === "dark") {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require("@/assets/images/pages/login-v2-dark.svg");
        return this.sideImg;
      }
      return this.sideImg;
    },
  },
  mounted() {
    if (this.$route.query.registered === '1') {
      sweetSuccess(
        'Registration successful',
        'Your company account has been created. Check your email for a verification code, then sign in.',
      );
      this.$router.replace({ path: '/login', query: {} });
    }
  },
  beforeDestroy() {
    this.clearResendTimer();
  },
  methods: {
    ...mapActions({
      login: "auth/login",
      loadUser: "auth/loadUser",
      sanctum: "auth/sanctum",
      verifyEmailCode: "auth/verifyEmailCode",
      resendEmailCode: "auth/resendEmailCode",
    }),
    continueAfterAuth() {
      this.loadUser(this.$ability).then((response) => {
        if (!response) {
          toastFailure('Could not load your account. Please try again.');
          this.isLoginLoading = false;
          return;
        }

        if (response.company != null) {
          applyCompanyWindowGlobals(response.company);
        }

        const staffRoles = ['Admin', 'Company Owner', 'Supervisor']
        const afterModules = staffRoles.includes(response.role)
          ? this.$store.dispatch('modulesRegistry/loadModules').catch(() => null)
          : Promise.resolve()

        afterModules.then(() => {
          if (response.role === 'Supervisor' && !response.last_password_change) {
            window.location.href = this.$router.resolve({ name: 'password' }).href;
            return;
          }

          window.location.href = this.$router.resolve({ name: 'admin' }).href;
        }).catch(() => {
          this.isLoginLoading = false;
        })
      }).catch(() => {
        toastFailure('Could not load your account. Please try again.');
        this.isLoginLoading = false;
      });
    },
    async submit() {
      if (!this.captchaToken) {
        toastFailure('Please refresh the question and try again.');
        this.isLoginLoading = false;
        return;
      }

      const credentials = {
        email: this.form.email,
        password: this.form.password,
        captcha_token: this.captchaToken,
        captcha_answer: Number(this.captchaAnswer),
        ...getBrowserAccessContext(),
      };
      if (this.selectedCompanyId) {
        credentials.company_id = this.selectedCompanyId;
      }

      try {
        await this.sanctum();
        const loginResponse = await this.login({ credentials, ability: this.$ability });
        const responseData = loginResponse && loginResponse.data ? loginResponse.data : loginResponse;

        if (responseData.status === 'multiple_accounts') {
          const available = Array.isArray(responseData.available) ? responseData.available : [];
          if (available.length) {
            this.availableAccounts = available;
          }
          this.$refs.loginCaptcha?.refresh();
          this.isLoginLoading = false;
          return;
        }

        this.availableAccounts = [];
        this.selectedCompanyId = null;

        if (responseData.status === 'email_verification_required') {
          this.needsEmailVerification = true;
          this.verificationEmail = responseData.email || this.form.email;
          this.verificationCode = '';
          this.verificationError = '';
          this.isLoginLoading = false;
          return;
        }

        // Keep the button disabled until redirect completes.
        this.continueAfterAuth();
      } catch (error) {
        const responseData = error?.response?.data;
        if (responseData?.errors || responseData?.message) {
          let message = responseData.message || 'The given data was invalid.';
          if (responseData.errors) {
            message = [message, ...Object.values(responseData.errors).flat()].join(' ');
          }
          toastFailure(message);
        } else {
          toastFailure('The given data was invalid.');
        }
        this.$refs.loginCaptcha?.refresh();
        this.isLoginLoading = false;
      }
    },
    validationForm() {
      if (this.isLoginLoading) {
        return;
      }
      this.isLoginLoading = true;
      this.submit();
    },
    loginWithAvailableAccount(account) {
      this.selectedCompanyId = account.company_id;
      this.validationForm();
    },
    async submitVerificationCode() {
      this.verificationError = '';
      const code = String(this.verificationCode || '').replace(/\D+/g, '');
      if (!code) {
        this.verificationError = 'Please enter the verification code.';
        return;
      }
      if (code.length !== 6) {
        this.verificationError = 'Please enter the 6-digit verification code.';
        return;
      }

      this.isVerifyLoading = true;
      try {
        await this.verifyEmailCode({ code });
        this.needsEmailVerification = false;
        this.continueAfterAuth();
      } catch (error) {
        const message = error?.response?.data?.message
          || 'Invalid verification code.';
        this.verificationError = message;
      } finally {
        this.isVerifyLoading = false;
      }
    },
    onVerificationCodeInput(value) {
      const raw = typeof value === 'string' ? value : String(this.verificationCode || '');
      this.verificationCode = raw.replace(/\D+/g, '').slice(0, 6);
    },
    async resendVerificationCode() {
      if (this.resendCooldown > 0) {
        return;
      }

      this.isResendLoading = true;
      try {
        await this.resendEmailCode();
        toastSuccess('A new verification code has been sent to your email.');
        this.startResendCooldown(60);
      } catch (error) {
        const message = error?.response?.data?.message
          || 'Could not resend the code. Please try again.';
        toastFailure(message);
        if (error?.response?.status === 429) {
          this.startResendCooldown(60);
        }
      } finally {
        this.isResendLoading = false;
      }
    },
    startResendCooldown(seconds) {
      this.clearResendTimer();
      this.resendCooldown = seconds;
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown <= 1) {
          this.clearResendTimer();
          this.resendCooldown = 0;
          return;
        }
        this.resendCooldown -= 1;
      }, 1000);
    },
    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },
    backToLogin() {
      this.needsEmailVerification = false;
      this.verificationCode = '';
      this.verificationError = '';
      this.clearResendTimer();
      this.resendCooldown = 0;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
